import React, { useEffect, useLayoutEffect, useState } from "react";
import useSelectorId from "../../../../utilities/hooks/useSelectorId";
import {
   ECLTable,
   ECLLoader,
   ECLThemeImage,
   ECLDropDown,
} from "../../../index";
import TableNumericRangeFilter from "../../../Widgets/ECLTable/widgets/TableNumericRangeFilter";
import TableStringFilter from "../../../Widgets/ECLTable/widgets/TableStringFilter";
import TableDateRangeFilter from "../../../Widgets/ECLTable/widgets/TableDateRangeFilter";
import * as arrow from "apache-arrow";
import { ECLUnsafeStringifyULID } from "../../../../utilities/ECLUUID";
import "./ECLTableBlock.css";
import {
   updateServerSession,
   endpointRequest,
} from "../../../../utilities/requests";
import debounce from "../../../../utilities/debounce";
import { translate } from "../../../../utilities/Translate";
import ECLDialog from "../../../Widgets/ECLDialog/ECLDialog";
import ECLBlockOptions from "../../components/ECLBlockOptions";

const numberTypes = [
   arrow.Type.Int,
   arrow.Type.Int8,
   arrow.Type.Int16,
   arrow.Type.Int32,
   arrow.Type.Int64,
   arrow.Type.Decimal,
   arrow.Type.Float,
   arrow.Type.Float16,
   arrow.Type.Float32,
   arrow.Type.Float64,
];

const dateTypes = [
   arrow.Type.Date,
   arrow.Type.DateMillisecond,
   arrow.Type.Timestamp,
];

const stringTypes = [
   arrow.Type.FixedSizeBinary,
   arrow.Type.Binary,
   arrow.Type.Bool,
   arrow.Type.Utf8,
];

export default function ECLTableBlock(props) {
   const instanceId = useSelectorId();
   const [table, setTable] = useState(null);
   const [error, setError] = useState(null);
   const [tableContainerWidth, setTableContainerWidth] = useState(0);
   const [modalContainerWidth, setModalContainerWidth] = useState(0);

   //Initial Setup of the table block
   useEffect(() => {
      const rootID = props.headlist.at(-1); //Last item on the headlist is your root
      const sessionID = props.flatDoc[rootID].session;
      endpointRequest(props.parentObject, props.block.id, sessionID, "AllData")
         .then(async (res) => {
            const columnsWithFiltered = await endpointRequest(
               props.parentObject,
               props.block.id,
               sessionID,
               "Filtered",
            );
            let defaultColumnFilters = await Promise.all(
               res.columns.map((c) =>
                  endpointRequest(
                     props.parentObject,
                     props.block.id,
                     sessionID,
                     c.name,
                  ),
               ),
            );
            let initialColumns = res.columns.map((c, i) => {
               let defaultUniques = null;
               if (
                  c.typeId === arrow.Type.Utf8 ||
                  c.typeId === arrow.Type.Bool
               ) {
                  return {
                     ...c,
                     filter: defaultColumnFilters[i].rows.map((f, ind) => {
                        return {
                           id: instanceId + "opt" + ind,
                           searchableString: String(f[0]),
                           jsx: String(f[0]).trim(),
                           selected: f[1],
                        };
                     }),
                     filtered: columnsWithFiltered.rows.find(
                        (x) => x[0] === String(c.name),
                     )?.[1],
                     defaultUniques: defaultColumnFilters[i].rows,
                  };
               }
               let filter = [];
               let defaultMin = null;
               let defaultMax = null;
               if (
                  numberTypes.includes(c.typeId) ||
                  dateTypes.includes(c.typeId)
               ) {
                  defaultMin = defaultColumnFilters[i].rows[0][2];
                  defaultMax = defaultColumnFilters[i].rows[0][3];
               }
               if (props.block.state?.[c.name]) {
                  if (numberTypes.includes(c.typeId)) {
                     filter = [defaultMin, defaultMax];
                  }
                  if (dateTypes.includes(c.typeId)) {
                     filter = [defaultMin, defaultMax];
                  }
               }
               return {
                  ...c,
                  filter: filter,
                  filtered: columnsWithFiltered.rows.find(
                     (x) => x[0] === String(c.name),
                  )?.[1],
                  defaultMin: defaultMin,
                  defaultMax: defaultMax,
                  defaultUniques: defaultUniques,
               };
            });
            setTable({
               columns: initialColumns,
               rows: res.rows.map((r) => {
                  return { row: r };
               }),
            });
         })
         .catch((error) => {
            setError("Table Failed to Load");
            console.error(error);
         });
   }, []);

   //Updated Table query
   useEffect(() => {
      const updateTable = async () => {
         const rootID = props.headlist.at(-1); // Last item on the headlist is your root
         const sessionID = props.flatDoc[rootID].session;
         try {
            const res = await endpointRequest(
               props.parentObject,
               props.block.id,
               sessionID,
               "AllData",
            );
            const columnsWithFiltered = await endpointRequest(
               props.parentObject,
               props.block.id,
               sessionID,
               "Filtered",
            );
            const defaultColumnFilters = await Promise.all(
               res.columns.map((c) =>
                  endpointRequest(
                     props.parentObject,
                     props.block.id,
                     sessionID,
                     c.name,
                  ),
               ),
            );
            setTable((t) => {
               return {
                  ...t,
                  rows: res.rows.map((r) => ({ row: r })),
                  columns: t.columns.map((c, i) => {
                     let min, max;
                     if (
                        dateTypes.includes(c.type.typeId) ||
                        numberTypes.includes(c.type.typeId)
                     ) {
                        min = defaultColumnFilters[i].rows[0][0];
                        max = defaultColumnFilters[i].rows[0][1];
                     }
                     if (stringTypes.includes(c.type.typeId)) {
                        const filterState = {};
                        c.filter.forEach((f) => {
                           filterState[f.searchableString] = f.selected;
                        });
                        return {
                           ...c,
                           defaultMin: min < c.defaultMin ? min : c.defaultMin,
                           defaultMax: max > c.defaultMax ? max : c.defaultMax,
                           defaultUniques: defaultColumnFilters[i].rows,
                           filter: defaultColumnFilters[i].rows.map(
                              (f, ind) => {
                                 return {
                                    id: instanceId + "opt" + ind,
                                    searchableString: String(f[0]),
                                    jsx: String(f[0]).trim(),
                                    selected: filterState[String(f[0])],
                                 };
                              },
                           ),
                           filtered: columnsWithFiltered.rows.find(
                              (x) => x[0] === String(c.name),
                           )?.[1],
                        };
                     }
                     return {
                        ...c,
                        filtered: columnsWithFiltered.rows.find(
                           (x) => x[0] === String(c.name),
                        )?.[1],
                        defaultMin: defaultColumnFilters[i].rows[0][0],
                        defaultMax: defaultColumnFilters[i].rows[0][1],
                        defaultUniques: defaultColumnFilters[i].rows,
                     };
                  }),
               };
            });
         } catch (err) {
            console.error("Error updating table:", err);
            setError("Table update failed");
         }
      };
      if (table) {
         updateTable();
      }
   }, [props.block.react.refresh]);

   //Adjust Size based on container size
   useLayoutEffect(() => {
      const tableContainer = document.querySelector(
         "#table-block" + instanceId,
      );
      if (tableContainer) {
         setTableContainerWidth(
            tableContainer.getBoundingClientRect().width - 20,
         );
      }
      if (window.innerWidth > 768 && window.innerHeight > 768) {
         setModalContainerWidth(window.innerWidth * 0.8 - 40 - 5 - 2); // 40pxs of padding, 5 px for scroll bar, 2px for borders
      } else {
         setModalContainerWidth(window.innerWidth - 60);
      }

      //Buncha resizing garbage
      const resizeFunc = debounce(() => {
         const tableContainer = document.querySelector(
            "#table-block" + instanceId,
         );
         const modalContainer = document.querySelector(
            "#table-block-modal" + instanceId,
         );
         if (tableContainer) {
            setTableContainerWidth(
               tableContainer.getBoundingClientRect().width - 20,
            );
         }
         if (modalContainer) {
            setModalContainerWidth(
               modalContainer.getBoundingClientRect().width - 20,
            );
         }
      }, 1000);
      //resize if we toggle the appWidthContainer
      const appWidthContainer = document.querySelector("#app-width-container");
      if (appWidthContainer) {
         appWidthContainer.addEventListener("widthToggled", resizeFunc);
      }
      //resize if we resize the window
      window.addEventListener("resize", resizeFunc);
      return () => {
         window.removeEventListener("resize", resizeFunc);
         const appWidthContainer = document.querySelector(
            "#app-width-container",
         );
         if (appWidthContainer) {
            appWidthContainer.removeEventListener("widthToggled", resizeFunc);
         }
      };
   }, [modalContainerWidth]);

   //if there are no columns, it's either a big error or an empty relation.
   if (props.block.data.columns.length < 1) {
      return (
         <>
            <label htmlFor="empty-table-relation">
               {props.block.data.name}
            </label>
            <p id="empty-table-relation">No Table Data</p>
         </>
      );
   }

   //Build our table
   const tableOrLoader = (tableType) =>
      table && !error ? (
         <ECLTable
            rowData={table.rows}
            columnSettings={table.columns.map((c, i) => {
               let precision;
               let cellWidth = 0;
               let shownColCount = props.block.data.columns.filter(
                  (c) => c.shown,
               ).length;
               if (
                  props.block.data.columns.find((d) => d.columnName === c.name)
                     ?.shown
               ) {
                  cellWidth =
                     tableType === "modal"
                        ? modalContainerWidth / shownColCount
                        : tableContainerWidth / shownColCount;
               }
               cellWidth = cellWidth < 150 ? 150 : cellWidth;

               //Dates and numbers are sooo close.
               let numericalProps = {
                  min: c.defaultMin,
                  max: c.defaultMax,
                  defaultValueMin: c.filter?.[0] ? c.filter[0] : c.defaultMin,
                  defaultValueMax: c.filter?.[1] ? c.filter[1] : c.defaultMax,
                  setRangeFunction: (rangeArray) => {
                     //Update State
                     setTable({
                        ...table,
                        columns: table.columns.map((col, index) => {
                           if (index === i) {
                              return {
                                 ...col,
                                 filter: rangeArray,
                              };
                           }
                           return col;
                        }),
                     });
                     //Update Server
                     let key_vals = [];
                     if (dateTypes.includes(c.type.typeId)) {
                        key_vals.push({
                           key: c.name,
                           value: {
                              filter: {
                                 datetime_min: rangeArray[0],
                                 datetime_max: rangeArray[1],
                              },
                           },
                        });
                     } else {
                        key_vals.push({
                           key: c.name,
                           value: {
                              filter: {
                                 min: rangeArray[0],
                                 max: rangeArray[1],
                              },
                           },
                        });
                     }
                     updateServerSession(props, key_vals);
                  },
                  clearRangeFunction: () => {
                     setTable({
                        ...table,
                        columns: table.columns.map((col, index) => {
                           if (index === i) {
                              return {
                                 ...col,
                                 filter: [c.defaultMin, c.defaultMax],
                              };
                           }
                           return col;
                        }),
                     });
                     // Update Server
                     let key_vals = [];
                     if (dateTypes.includes(c.type.typeId)) {
                        key_vals.push({
                           key: c.name,
                           value: {
                              filter: {
                                 datetime_min: new Date(
                                    c.defaultMin,
                                 ).toISOString(),
                                 datetime_max: new Date(
                                    c.defaultMax,
                                 ).toISOString(),
                                 wasReset: true,
                              },
                           },
                        });
                     } else {
                        key_vals.push({
                           key: c.name,
                           value: {
                              filter: {
                                 min: c.defaultMin,
                                 max: c.defaultMax,
                                 wasReset: true,
                              },
                           },
                        });
                     }
                     updateServerSession(props, key_vals);
                  },
               };

               switch (c.type.typeId) {
                  case arrow.Type.Int:
                  case arrow.Type.Int8:
                  case arrow.Type.Int16:
                  case arrow.Type.Int32:
                  case arrow.Type.Int64:
                     precision = 0;
                  case arrow.Type.Decimal:
                  case arrow.Type.Float:
                  case arrow.Type.Float16:
                  case arrow.Type.Float32:
                  case arrow.Type.Float64:
                     if (precision == undefined) {
                        precision = 2;
                     }
                     return {
                        label: c.name,
                        id: c.name,
                        width: cellWidth,
                        filter: (
                           <ECLDropDown
                              containerId={"app-width-container"}
                              inModal={tableType}
                              trigger={
                                 <>
                                    {c.filtered ? (
                                       <ECLThemeImage
                                          fontAwesome="filter"
                                          colorOverride="#00C08B"
                                          style={{
                                             width: "20px",
                                             height: "20px",
                                          }}
                                       />
                                    ) : (
                                       <ECLThemeImage
                                          fontAwesome="filter"
                                          style={{
                                             width: "20px",
                                             height: "20px",
                                          }}
                                       />
                                    )}
                                 </>
                              }
                              menuWidth={275}
                           >
                              <TableNumericRangeFilter
                                 precision={precision}
                                 {...numericalProps}
                              />
                           </ECLDropDown>
                        ),
                        filtered: c.filtered,
                     };
                  case arrow.Type.Date:
                  case arrow.Type.DateMillisecond:
                  case arrow.Type.Timestamp:
                     return {
                        label: c.name,
                        id: c.name,
                        width: cellWidth,
                        filter: (
                           <ECLDialog
                              title={"Column Filter"}
                              triggerClass={"table-header-modal-trigger"}
                              triggerText={
                                 <>
                                    {c.filtered ? (
                                       <ECLThemeImage
                                          fontAwesome="filter"
                                          colorOverride="#00C08B"
                                       />
                                    ) : (
                                       <ECLThemeImage fontAwesome="filter" />
                                    )}
                                 </>
                              }
                              customButtons={{}}
                           >
                              <TableDateRangeFilter {...numericalProps} />
                           </ECLDialog>
                        ),
                        filtered: c.filtered,
                     };
                  case arrow.Type.Binary:
                  case arrow.Type.FixedSizeBinary:
                     return {
                        label: c.name,
                        id: c.name,
                        width: cellWidth,
                        customFormatting: (rowIndex, value) => {
                           return (
                              <p
                                 title={ECLUnsafeStringifyULID(value)}
                                 className="string-cell cell-trunc"
                              >
                                 {ECLUnsafeStringifyULID(value)}
                              </p>
                           );
                        },
                     };
                  case arrow.Type.Bool:
                  case arrow.Type.Utf8:
                     return {
                        label: c.name,
                        id: c.name,
                        width: cellWidth,
                        filter: (
                           <TableStringFilter
                              options={c.filter}
                              inModal={tableType === "modal"}
                              containerId={
                                 tableType === "modal"
                                    ? "table-block-modal" + instanceId
                                    : "table-block-table" + instanceId
                              }
                              customTrigger={
                                 c.filtered ? (
                                    <ECLThemeImage
                                       fontAwesome="filter"
                                       colorOverride="#00C08B"
                                       style={{ width: "20px", height: "20px" }}
                                    />
                                 ) : (
                                    <ECLThemeImage
                                       fontAwesome="filter"
                                       alt={translate(`${c.name} filter`)}
                                    />
                                 )
                              }
                              setFilterFunction={(passedFilterArray) => {
                                 setTable({
                                    ...table,
                                    columns: table.columns.map((col, index) => {
                                       if (index === i) {
                                          return {
                                             ...col,
                                             filter: passedFilterArray,
                                          };
                                       }
                                       return col;
                                    }),
                                 });
                                 //Update Server
                                 let key_vals = [];
                                 key_vals.push({
                                    key: c.name,
                                    value: {
                                       filter: {
                                          selections: passedFilterArray
                                             .filter((s) => s.selected)
                                             .map((s) => s.searchableString),
                                       },
                                    },
                                 });
                                 updateServerSession(props, key_vals);
                              }}
                           />
                        ),
                        filtered: c.filtered,
                        type: "select",
                     };
                  default:
                     return {
                        label: c.name,
                        id: c.name,
                        width: cellWidth,
                     };
               }
            })}
            tableSettings={{
               columnOrder: props.block.data.columns
                  .filter((c) => c.shown)
                  .map((c) => c.columnName),
               rowHeight: 35,
            }}
         />
      ) : (
         <ECLLoader message={error} />
      );

   return (
      <div className={"table-block-container"} id={"table-block" + instanceId}>
         <ECLBlockOptions
            block={props.block}
            instanceId={instanceId}
            expandWidget={tableOrLoader("modal")}
         />
         <div className="table-block-content-box">
            <div
               id={"table-block-table" + instanceId}
               className="ecl-table-block-container"
               style={{ width: "100%", height: "100%" }}
            >
               {tableOrLoader("")}
            </div>
         </div>
      </div>
   );
}
